import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import apiClient from '../api/client';
import './UploadPanel.css';

const UploadPanel = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState(null);
  const [lastUploadedItems, setLastUploadedItems] = useState([]);
  const abortControllerRef = useRef(null);
  const queryClient = useQueryClient();

  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setMessage(null);
    setUploadProgress(0);
    setLastUploadedItems([]);
  };

  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsUploading(false);
      setMessage({ type: 'info', text: 'Upload cancelled.' });
    }
  };

  const handleUndoUpload = async () => {
    if (lastUploadedItems.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete the ${lastUploadedItems.length} images you just uploaded?`)) {
      return;
    }

    try {
      const ids = lastUploadedItems.map(item => item.id);
      await apiClient.post('/media/bulk-delete', { ids });
      
      setLastUploadedItems([]);
      setMessage({ type: 'success', text: 'Upload undone. Images deleted.' });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    } catch (error) {
      console.error('Undo error:', error);
      setMessage({ type: 'error', text: 'Failed to undo upload.' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selectedFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select files to upload.' });
      return;
    }

    // Create new AbortController
    abortControllerRef.current = new AbortController();

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setMessage(null);
      setLastUploadedItems([]);

      // 1. Get Signature from Server
      const signResponse = await apiClient.get('/media/sign-upload', {
        signal: abortControllerRef.current.signal
      });
      const { signature, timestamp, cloudName, apiKey, folder } = signResponse.data;

      const uploadedItems = [];
      const totalFiles = selectedFiles.length;
      let totalLoaded = 0;
      const fileSizes = selectedFiles.map(f => f.size);
      const totalSize = fileSizes.reduce((a, b) => a + b, 0);

      // 2. Upload each file to Cloudinary directly
      for (let i = 0; i < totalFiles; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', apiKey);
        formData.append('timestamp', timestamp);
        formData.append('signature', signature);
        formData.append('folder', folder);

        // Track progress for this file
        let lastFileLoaded = 0;

        const cloudinaryRes = await axios.post(
          `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
          formData,
          {
            signal: abortControllerRef.current.signal,
            // eslint-disable-next-line no-loop-func
            onUploadProgress: (progressEvent) => {
              const loadedDelta = progressEvent.loaded - lastFileLoaded;
              lastFileLoaded = progressEvent.loaded;
              totalLoaded += loadedDelta;
              
              const percentCompleted = Math.round((totalLoaded * 100) / totalSize);
              setUploadProgress(percentCompleted);
            }
          }
        );

        uploadedItems.push({
          cloudinaryData: cloudinaryRes.data,
          originalName: file.name,
          mimeType: file.type
        });
      }

      // 3. Save metadata to server
      setMessage({ type: 'info', text: 'Processing uploads...' });
      const response = await apiClient.post('/media/save-metadata', { uploads: uploadedItems }, {
        signal: abortControllerRef.current.signal
      });
      
      setSelectedFiles([]);
      setUploadProgress(0);
      setLastUploadedItems(response.data.items || []);
      setMessage({ type: 'success', text: `Successfully uploaded ${response.data.items?.length || 0} files.` });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        setMessage({ type: 'info', text: 'Upload cancelled.' });
      } else {
        console.error('Upload error:', error);
        const errorMessage =
          error.response?.data?.message || error.message || 'Failed to upload files. Please try again.';
        setMessage({ type: 'error', text: errorMessage });
      }
    } finally {
      setIsUploading(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <section className="panel">
      <h2>Upload Images</h2>
      <p className="info-text">Upload images for annotation. Only admin users can upload images.</p>
      <form className="upload-form" onSubmit={handleSubmit}>
        <div className="upload-actions">
          <label className="file-input">
            <span>Select files to upload</span>
            <input type="file" multiple onChange={handleFileChange} />
          </label>
          
          {isUploading ? (
            <button type="button" className="danger" onClick={handleCancelUpload}>
              Cancel Upload
            </button>
          ) : (
            <button type="submit" className="primary" disabled={selectedFiles.length === 0}>
              Upload Files
            </button>
          )}

          {lastUploadedItems.length > 0 && !isUploading && (
            <button type="button" className="secondary" onClick={handleUndoUpload}>
              Undo Last Upload ({lastUploadedItems.length})
            </button>
          )}
        </div>

        {isUploading && (
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
            <span className="progress-text">{uploadProgress}%</span>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <ul className="file-list">
            {selectedFiles.map((file) => (
              <li key={file.name}>
                {file.name} <small>({Math.round(file.size / 1024)} KB)</small>
              </li>
            ))}
          </ul>
        )}
      </form>

      {message && <p className={`message ${message.type}`}>{message.text}</p>}
    </section>
  );
};

export default UploadPanel;
